import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { useCustomerAuth } from '../lib/customerAuth';
import { useCart } from '../lib/cart';
import { Logo } from './Logo';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from './ui/dropdown-menu';
import { Menu, ShoppingCart, User, Search, ChevronDown, ChevronRight, LogOut, Package as PackageIcon, ClipboardList } from 'lucide-react';
import LoginModal from './LoginModal';

function MegaMenu({ tree, onNavigate }) {
    const [openId, setOpenId] = useState(null);
    return (
        <nav className="hidden lg:flex items-center gap-1" data-testid="mega-menu">
            <Link to="/" className="px-3 py-2 text-sm text-white hover:bg-white/10 rounded-md">Home</Link>
            {tree.map((cat) => (
                <div key={cat.id} className="relative" onMouseEnter={() => setOpenId(cat.id)} onMouseLeave={() => setOpenId(null)}>
                    <button className="px-3 py-2 text-sm text-white hover:bg-white/10 rounded-md flex items-center gap-1" data-testid={`mega-cat-${cat.slug}`}>
                        {cat.name}
                        {cat.children?.length > 0 && <ChevronDown className="h-3.5 w-3.5" />}
                    </button>
                    {openId === cat.id && cat.children?.length > 0 && (
                        <div className="absolute left-0 top-full mt-0.5 min-w-[280px] bg-white rounded-lg shadow-lg border py-2 z-50">
                            {cat.children.map(child => (
                                <Link key={child.id} to={`/category/${child.slug}`} className="block px-4 py-2 text-sm text-neutral-800 hover:bg-neutral-50" onClick={() => { setOpenId(null); onNavigate && onNavigate(); }}>
                                    {child.name}
                                </Link>
                            ))}
                        </div>
                    )}
                </div>
            ))}
            <Link to="/services" className="px-3 py-2 text-sm text-white hover:bg-white/10 rounded-md" data-testid="mega-services">Services</Link>
            <Link to="/about" className="px-3 py-2 text-sm text-white hover:bg-white/10 rounded-md">About</Link>
        </nav>
    );
}

function MobileMenu({ tree, onClose }) {
    const [expanded, setExpanded] = useState({});
    const toggle = (id) => setExpanded(m => ({ ...m, [id]: !m[id] }));
    return (
        <div className="py-2" data-testid="mobile-nav">
            <div className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-neutral-400">Menu</div>
            <Link to="/" onClick={onClose} className="flex items-center gap-2 px-3 py-2.5 text-sm font-medium hover:bg-neutral-50 rounded" data-testid="mobile-nav-home">Home</Link>

            <div className="px-3 py-2 mt-2 text-[11px] font-semibold uppercase tracking-wider text-neutral-400">Shop by Category</div>
            {tree.map(cat => {
                const hasKids = (cat.children?.length || 0) > 0;
                const open = !!expanded[cat.id];
                return (
                    <div key={cat.id} className="mb-0.5">
                        <div className="flex items-center gap-2 rounded hover:bg-neutral-50">
                            <Link
                                to={`/category/${cat.slug}`}
                                onClick={onClose}
                                className="flex items-center gap-2 flex-1 min-w-0 px-3 py-2.5 text-sm font-semibold text-neutral-900"
                                data-testid={`mobile-cat-${cat.slug}`}
                            >
                                {cat.image ? (
                                    <img src={cat.image} alt="" className="h-9 w-9 rounded-md object-cover border shrink-0" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                                ) : (
                                    <span className="h-9 w-9 rounded-md bg-green-50 flex items-center justify-center shrink-0">
                                        <ChevronRight className="h-4 w-4 text-green-700" />
                                    </span>
                                )}
                                <span className="truncate">{cat.name}</span>
                            </Link>
                            {hasKids && (
                                <button
                                    onClick={() => toggle(cat.id)}
                                    className="pr-3 p-2 text-neutral-500"
                                    aria-label={open ? 'Collapse' : 'Expand'}
                                    data-testid={`mobile-cat-toggle-${cat.slug}`}
                                >
                                    <ChevronDown className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`} />
                                </button>
                            )}
                        </div>
                        {hasKids && open && (
                            <div className="ml-11 mb-1 border-l border-neutral-200 pl-2">
                                {cat.children.map(child => (
                                    <Link
                                        key={child.id}
                                        to={`/category/${child.slug}`}
                                        onClick={onClose}
                                        className="flex items-center gap-2 px-3 py-2 text-sm text-neutral-700 hover:bg-neutral-50 rounded"
                                        data-testid={`mobile-subcat-${child.slug}`}
                                    >
                                        {child.image ? (
                                            <img src={child.image} alt="" className="h-6 w-6 rounded object-cover border shrink-0" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                                        ) : (
                                            <span className="h-1.5 w-1.5 rounded-full bg-neutral-400 mr-1.5 ml-1" />
                                        )}
                                        <span className="truncate">{child.name}</span>
                                    </Link>
                                ))}
                            </div>
                        )}
                    </div>
                );
            })}

            <div className="mt-3 border-t pt-2">
                <div className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-neutral-400">More</div>
                <Link to="/services" onClick={onClose} className="block px-3 py-2.5 text-sm hover:bg-neutral-50 rounded">Services</Link>
                <Link to="/wholesale" onClick={onClose} className="block px-3 py-2.5 text-sm hover:bg-neutral-50 rounded">B2B / Wholesale</Link>
                <Link to="/about" onClick={onClose} className="block px-3 py-2.5 text-sm hover:bg-neutral-50 rounded">About</Link>
                <Link to="/contact" onClick={onClose} className="block px-3 py-2.5 text-sm hover:bg-neutral-50 rounded">Contact</Link>
            </div>
        </div>
    );
}

export default function CustomerHeader() {
    const nav = useNavigate();
    const { customer, isAuthed, logout } = useCustomerAuth();
    const { totalQty } = useCart();
    const [tree, setTree] = useState([]);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [loginOpen, setLoginOpen] = useState(false);
    const [q, setQ] = useState('');

    useEffect(() => {
        api.get('/categories/tree').then(r => {
            // Hide deactivated categories & sub-categories from the storefront header
            const filterActive = (nodes) => (nodes || [])
                .filter(n => n && n.active !== false)
                .map(n => ({ ...n, children: filterActive(n.children) }));
            setTree(filterActive(r.data || []));
        }).catch(() => setTree([]));
    }, []);

    const doSearch = () => {
        if (q.trim()) nav(`/search?q=${encodeURIComponent(q.trim())}`);
    };

    return (
        <>
            {/* Blue header */}
            <header className="brand-blue-header sticky top-0 z-40 shadow-sm border-b border-white/10" data-testid="customer-header">
                <div className="max-w-[1400px] mx-auto px-3 sm:px-6">
                    <div className="h-16 flex items-center gap-3">
                        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                            <SheetTrigger asChild>
                                <Button variant="ghost" size="icon" className="lg:hidden text-white hover:bg-white/10" data-testid="customer-mobile-menu">
                                    <Menu className="h-5 w-5" />
                                </Button>
                            </SheetTrigger>
                            <SheetContent side="left" className="w-80 p-3 overflow-y-auto">
                                <MobileMenu tree={tree} onClose={() => setMobileOpen(false)} />
                            </SheetContent>
                        </Sheet>
                        <Link to="/" data-testid="customer-logo-link"><Logo size={40} /></Link>
                        <div className="hidden md:flex flex-1 max-w-lg mx-4 items-center bg-white/10 rounded-md px-3 py-2">
                            <Search className="h-4 w-4 text-white/70 mr-2" />
                            <input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') doSearch(); }}
                                placeholder="Search for plants, pots, tools..."
                                className="bg-transparent text-white text-sm outline-none flex-1 placeholder:text-white/60"
                                data-testid="customer-search-input" />
                        </div>
                        <div className="ml-auto flex items-center gap-1">
                            <Link to="/cart" className="relative text-white p-2 hover:bg-white/10 rounded-md" data-testid="customer-cart-link">
                                <ShoppingCart className="h-5 w-5" />
                                {totalQty > 0 && (
                                    <span className="absolute -top-1 -right-1 bg-brand-green text-white text-xs rounded-full h-5 min-w-[20px] px-1 flex items-center justify-center"
                                        style={{ background: 'hsl(142 71% 45%)' }} data-testid="cart-count-badge">
                                        {totalQty}
                                    </span>
                                )}
                            </Link>
                            {isAuthed ? (
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <button className="flex items-center gap-1.5 px-2 py-2 rounded-md hover:bg-white/10 text-white" data-testid="customer-user-menu">
                                            <User className="h-5 w-5" />
                                            <span className="hidden sm:inline text-sm">{customer?.name?.split(' ')[0]}</span>
                                        </button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="w-56">
                                        <DropdownMenuLabel>
                                            <div className="font-semibold">{customer?.name}</div>
                                            <div className="text-xs text-neutral-500">{customer?.mobile}</div>
                                            {customer?.type === 'wholesale' && (
                                                <div className="text-xs text-green-700 font-medium mt-1">Wholesale L{customer.level}</div>
                                            )}
                                        </DropdownMenuLabel>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem onClick={() => nav('/account')} data-testid="customer-account-link">
                                            <User className="h-4 w-4 mr-2" /> My Account
                                        </DropdownMenuItem>
                                        <DropdownMenuItem onClick={() => nav('/account/orders')} data-testid="customer-orders-link">
                                            <PackageIcon className="h-4 w-4 mr-2" /> My Orders
                                        </DropdownMenuItem>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem onClick={logout}>
                                            <LogOut className="h-4 w-4 mr-2" /> Sign out
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            ) : (
                                <Button variant="ghost" className="text-white hover:bg-white/10" onClick={() => setLoginOpen(true)} data-testid="customer-login-button">
                                    <User className="h-4 w-4 sm:mr-1" /><span className="hidden sm:inline">Sign In</span>
                                </Button>
                            )}
                        </div>
                    </div>
                    <MegaMenu tree={tree} />
                </div>
            </header>
            <LoginModal open={loginOpen} onOpenChange={setLoginOpen} />
        </>
    );
}
